import { type ChangeEvent as ReactChangeEvent, useState, useEffect } from 'react';
import { Colors } from '~/themes/colors';
import { Label } from '../Label';
import { Row } from '../Row';
import type { INumberFieldProps } from './Types';
import { formatNumberInput } from '~/utils/String';

// Format number with commas for display
const formatNumberWithCommas = (value: string): string => {
  if (!value) return '';

  // Handle decimal values
  const parts = value.split('.');
  const integerPart = parts[0];
  const decimalPart = parts[1];

  // Add commas to integer part
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  // Combine with decimal part if exists
  return decimalPart !== undefined ? `${formattedInteger}.${decimalPart}` : formattedInteger;
};

export const NumberField = ({
  label,
  value,
  setValue,
  placeholder,
  currency = false,
  customInputStyle = {},
  required = false,
  error = false,
  allowDecimals = false,
  maxDecimals = 2,
  dataCy,
  dataTestId,
}: INumberFieldProps) => {
  const [displayValue, setDisplayValue] = useState('');

  // Update display value when value prop changes
  useEffect(() => {
    if (currency) {
      setDisplayValue(formatNumberWithCommas(value));
    } else {
      setDisplayValue(value);
    }
  }, [value, currency]);

  const handleChange = (e: ReactChangeEvent<HTMLInputElement>): void => {
    const inputValue = e.target.value;

    // Format the input to get clean numeric value
    const cleanValue = formatNumberInput(inputValue, allowDecimals, maxDecimals);

    // Update the actual value (without formatting)
    setValue(cleanValue);

    // Update display value
    if (currency) {
      setDisplayValue(formatNumberWithCommas(cleanValue));
    } else {
      setDisplayValue(cleanValue);
    }
  };

  return (
    <div>
      {label && (
        <Row gap={4}>
          <Label text={label} required={required} />
        </Row>
      )}
      <input
        data-cy={dataCy}
        data-testid={dataTestId}
        className="input-field"
        style={{
          margin: 0,
          width: 252,
          ...(error && {
            borderWidth: 1.5,
            borderStyle: 'solid',
            borderColor: Colors.error_400,
            backgroundColor: Colors.error_background,
          }),
          ...customInputStyle,
        }}
        type="text"
        inputMode="numeric"
        value={displayValue}
        placeholder={placeholder || label || ''}
        onChange={handleChange}
      />
    </div>
  );
};
